import React, {
  DetailedHTMLProps,
  InputHTMLAttributes,
  useEffect,
  useState,
} from "react";
import cn from "classnames";
import { IWrappingInput } from "@types";
import { useTranslations } from "next-intl";
import { useAppContext } from "@context/app";
import { useRouter } from "next/router";
import { convertStringNumber } from "@utils/digits";
import ImagePrepare from "@elements/imagePrepare";
import Carousel from "@elements/carousel";
import { SwiperSlide } from "swiper/react";
interface RadioGroupProps
  extends Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    "ref"
  > {
  fieldName: string;
  label?: string;
  options: IWrappingInput[];
  type?: string;
  isRequired?: boolean;
  error?: string;
  placeholder?: string;
  className?: string;
  id?: string;
  inputClassName?: string;
  noLabelPlaceholder?: boolean;
  maxLength?: number;
  selected?: IWrappingInput;
  reset?: boolean;
}

const RadioGroup = React.forwardRef<HTMLInputElement, RadioGroupProps>(
  (
    {
      fieldName,
      error = "",
      isRequired = false,
      label = "",
      options,
      type,
      placeholder,
      className,
      id,
      inputClassName,
      noLabelPlaceholder = false,
      maxLength,
      selected,
      reset,
      ...props
    },
    ref
  ) => {
    const { decimal, currency } = useAppContext();
    const [selectedOptions, setSelectedOptions] = useState<IWrappingInput>();
    const t = useTranslations();
    const { locale } = useRouter();


    useEffect(() => {
      setSelectedOptions(options.find((w) => w.id === selected?.id));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    useEffect(() => {
      setSelectedOptions(options.find((w) => w.id === selected?.id));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reset]);

    const handleChange = (option: IWrappingInput) => {
      let nextSelectedOptions;

      if (selectedOptions?.id === option.id) {
        nextSelectedOptions = undefined;
      } else {
        nextSelectedOptions = option;
      }

      setSelectedOptions(nextSelectedOptions);
      props.onChange?.(nextSelectedOptions as any);
    };

    const renderInput = (option: IWrappingInput) => {
      let isChecked;
      isChecked = selectedOptions?.id === option.id;
      const fieldId = `radio-${option.id}${id || ""}`;
      const fieldClassName = cn(
        "w-[124px] h-[124px] m-auto flex justify-center items-center transition-all relative",
        isChecked ? "border-2 border-[#008AF6] rounded-xl" : ""
      );

      return (
        <div className={fieldClassName} key={id}>
          <label
            htmlFor={fieldId}
            className=" flex-1 h-full flex items-center justify-between cursor-pointer px-3 relative"
          >
            <input
              type="radio"
              className="opacity-0 absolute w-0 h-0 inset-0 "
              id={fieldId}
              name={`${fieldName}`}
              value={option.id}
              onChange={() => { }}
              onClick={() => handleChange(option)}
              defaultChecked={isChecked}
            />
            <div
              className={`relative  rounded-xl overflow-hidden cursor-pointer transition-all  ${isChecked ? " w-28 h-28" : " w-[120px] h-[120px]"
                }`}
            >
              <ImagePrepare media={option.media} alt={option.name} width={120} />
            </div>
          </label>
        </div>
      );
    };

    const fields = options.map((option) => (
      <SwiperSlide key={option.id} className="w-max">
        <li
          className=" w-max"
          key={`radio-${option.id}`}
        >
          {renderInput(option)}
          <p className="text-center text-[#999] text-sm font-normal mt-2 left-to-right">
            {option.price.price ? (
              <>
                <span style={{ direction: "rtl" }} className="text-left">
                  {currency}{" "}
                </span>{" "}
                <span className="px-1">
                  {convertStringNumber(locale, option.price.price.toFixed(decimal))}
                  {/* {locale?.includes("ar")
                    ? toArabicNumber(option.price.price.toFixed(decimal))
                    : option.price.price.toFixed(decimal)} */}
                </span>
              </>
            ) : (
              t("client_giftoptons_free")
            )}
          </p>
        </li>
      </SwiperSlide>
    ));

    return (
      <div className={cn("flex flex-col w-full", className)}>
        {!noLabelPlaceholder || label ? (
          <label
            className={cn(
              "text-[13px] text-dark2 w-max tracking-normal   font-medium"
            )}
          >
            {label}
          </label>
        ) : null}
        <div className={cn("relative w-full")}>
          <ul className=" flex flex-wrap tabletSc:justify-around  ">
            <Carousel gap={8}> {fields}</Carousel>

          </ul>
        </div>
        {error && (
          <span
            className={cn(
              "font-normal text-left whitespace-nowrap ml-4 text-sm mt-2 text-red-500"
            )}
          >
            {error}
          </span>
        )}
      </div>
    );
  }
);

RadioGroup.displayName = "RadioGroup";

export default RadioGroup;
